import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import fr from './i18n/translations/fr.json';
import en from './i18n/translations/en.json';
import es from './i18n/translations/es.json';
import de from './i18n/translations/de.json';
import it from './i18n/translations/it.json';
import nl from './i18n/translations/nl.json';
import pt from './i18n/translations/pt.json';

const STORAGE_KEY = 'bfi_language';

export const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
];

const TRANSLATIONS = { fr, en, es, de, it, nl, pt };
const SUPPORTED_CODES = LANGUAGES.map((l) => l.code);
const DEFAULT_LANG = 'fr';

function detectBrowserLanguage() {
  if (typeof window === 'undefined' || !window.navigator) return DEFAULT_LANG;
  const langs = [
    ...(navigator.languages || []),
    navigator.language || '',
    navigator.userLanguage || '',
  ].filter(Boolean);
  for (const raw of langs) {
    const code = String(raw).slice(0, 2).toLowerCase();
    if (SUPPORTED_CODES.includes(code)) return code;
  }
  return DEFAULT_LANG;
}

function getNested(obj, path) {
  if (!obj || !path) return undefined;
  const parts = path.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur && typeof cur === 'object' && p in cur) cur = cur[p];
    else return undefined;
  }
  return cur;
}

function interpolate(str, vars) {
  if (typeof str !== 'string' || !vars) return str;
  return str.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ''));
}

const LanguageContext = createContext({
  language: DEFAULT_LANG,
  setLanguage: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LANG;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED_CODES.includes(saved)) return saved;
    } catch {}
    return detectBrowserLanguage();
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, language); } catch {}
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', language);
    }
  }, [language]);

  const setLanguage = useCallback((code) => {
    if (SUPPORTED_CODES.includes(code)) setLanguageState(code);
  }, []);

  const t = useCallback((key, vars) => {
    if (key == null) return '';
    const current = TRANSLATIONS[language] || TRANSLATIONS[DEFAULT_LANG];
    let value = getNested(current, key);
    if (value == null) value = getNested(TRANSLATIONS[DEFAULT_LANG], key);
    if (value == null) return key;
    if (typeof value === 'string') return interpolate(value, vars);
    return value;
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
